import { useEffect, useState } from "react";
import { initDatabase, getStats, getTrades, getSignals, getWatchlist, getPortfolioHistory, Trade, Signal, WatchlistItem, PortfolioSnapshot, getDatabaseInfo } from "../services/postgresService";

export default function DatabaseDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{ totalTrades: number; totalVolume: number; totalSignals: number; watchlistCount: number } | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [history, setHistory] = useState<PortfolioSnapshot[]>([]);
  const [activeTab, setActiveTab] = useState<"overview" | "trades" | "signals" | "watchlist">("overview");
  const [dbInfo, setDbInfo] = useState(getDatabaseInfo());

  useEffect(() => {
    const load = async () => {
      await initDatabase();
      setStats(await getStats());
      setTrades(await getTrades(10));
      setSignals(await getSignals(10));
      setWatchlist(await getWatchlist());
      setHistory(await getPortfolioHistory(7));
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <p className="text-gray-400">Loading database...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">💾 Database</h2>
        <span className="text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded">{dbInfo.type}</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {(["overview", "trades", "signals", "watchlist"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
              activeTab === tab ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-500 text-xs uppercase">Total Trades</p>
            <p className="text-white font-bold text-xl">{stats?.totalTrades || 0}</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-500 text-xs uppercase">Volume</p>
            <p className="text-white font-bold text-xl">${((stats?.totalVolume || 0) / 1000).toFixed(1)}K</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-500 text-xs uppercase">Signals</p>
            <p className="text-white font-bold text-xl">{stats?.totalSignals || 0}</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3">
            <p className="text-gray-500 text-xs uppercase">Watchlist</p>
            <p className="text-white font-bold text-xl">{stats?.watchlistCount || 0}</p>
          </div>
        </div>
      )}

      {/* Trades */}
      {activeTab === "trades" && (
        <div>
          {trades.length === 0 ? (
            <p className="text-gray-500 text-sm">No trades recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {trades.map((trade) => (
                <div key={trade.id} className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                  <div>
                    <span className="text-white font-medium">{trade.symbol}</span>
                    <span className={`ml-2 text-xs ${trade.side === "buy" ? "text-green-400" : "text-red-400"}`}>
                      {trade.side.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-mono">${trade.total.toFixed(2)}</p>
                    <p className="text-gray-500 text-xs">{trade.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Signals */}
      {activeTab === "signals" && (
        <div>
          {signals.length === 0 ? (
            <p className="text-gray-500 text-sm">No signals recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {signals.map((signal) => (
                <div key={signal.id} className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                  <div>
                    <span className="text-white font-medium">{signal.symbol}</span>
                    <span className="ml-2 text-gray-500 text-xs">{signal.signal_type}</span>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${signal.score >= 7 ? "text-green-400" : signal.score <= 3 ? "text-red-400" : "text-yellow-400"}`}>
                      {signal.score}/10
                    </span>
                    <p className="text-gray-500 text-xs">{signal.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Watchlist */}
      {activeTab === "watchlist" && (
        <div>
          {watchlist.length === 0 ? (
            <p className="text-gray-500 text-sm">Watchlist is empty.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {watchlist.map((item) => (
                <span key={item.id} className="bg-gray-900/50 text-white px-3 py-1.5 rounded-lg text-sm">
                  {item.symbol}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-gray-600 mt-4">
        Data stored locally in browser · Clear browser to reset
      </p>
    </div>
  );
}